import { deriveTextEmbroideryFontFamilyFromStyle } from '@syconium/little-miss-figgy'

import { fromPlacementId } from '../../../../../containers/EmbroideryContainer/combination-rules/placementIds'
import {
  isLightProductColor,
  isSufficientEmbroideryColorContrast,
} from '../../../../../containers/EmbroideryContainer/utils/unorganized'

import {
  GraphicPreviewMark,
  PreviewFlatlayOffset,
  PreviewPlacement,
  TextPreviewMark,
} from './previewMarks'
import { GraphicPlaceholder, GraphicSlot, Group, IconImage, LogoImage, Text } from './styles'

export type EmbroideryPreviewPlacementProps = {
  placement: PreviewPlacement
  offset: Pick<PreviewFlatlayOffset, 'x' | 'y' | 'rotation' | 'scale'>
  productColorHex: string | undefined
  /** Drawn in the text's thread color and font while a checked text placement has no text yet. */
  textPlaceholder: string
  /** Off for the close-up, which shows the marks flat against the garment color. */
  showThreadEffect?: boolean
  /**
   * Keeps the text on the spot, with a graphic hung above it, so adding one never moves the other.
   * Off centers the text and graphic together, for the close-up.
   */
  centerOnText?: boolean
  /** The close-up draws the marks at the design's fixed sizes instead of relative to the garment. */
  isCloseUp?: boolean
}

/**
 * Everything stitched at one placement, centered on its spot on the garment. Text sits on its
 * spot; an icon or logo sharing the placement sits directly above it, so adding one never moves
 * the other, except chest logos, which center with the text. The whole group takes the
 * placement's rotation, which tilts sleeve marks to follow the sleeve.
 */
export const EmbroideryPreviewPlacement: React.FC<EmbroideryPreviewPlacementProps> = ({
  placement: { placementId, text, graphic },
  offset,
  productColorHex,
  textPlaceholder,
  showThreadEffect = true,
  centerOnText = true,
  isCloseUp = false,
}) => {
  const isLightGarment = isLightProductColor(productColorHex ?? null)
  const { location } = fromPlacementId(placementId)

  return (
    <Group
      aria-hidden={true}
      data-placement-id={placementId}
      $isLightGarment={isLightGarment}
      $isCloseUp={isCloseUp}
      $isSleeve={location === 'SLEEVE'}
      $placementScale={offset.scale}
      style={{
        left: `${offset.x * 100}%`,
        top: `${offset.y * 100}%`,
        transform: `translate(-50%, -50%) rotateZ(${offset.rotation}deg)`,
      }}
    >
      {text && (
        <TextMark
          mark={text}
          productColorHex={productColorHex}
          placeholder={textPlaceholder}
          showThreadEffect={showThreadEffect}
        />
      )}
      {graphic && (
        <GraphicSlot
          $withText={text !== undefined}
          // Chest logo groups center as a whole on the spot; elsewhere the text holds the spot.
          $hangAboveText={centerOnText && !(location === 'CHEST' && graphic.typeKey === 'logo')}
        >
          <GraphicMark mark={graphic} showThreadEffect={showThreadEffect} />
        </GraphicSlot>
      )}
    </Group>
  )
}

const TextMark: React.FC<{
  mark: TextPreviewMark
  productColorHex: string | undefined
  placeholder: string
  showThreadEffect: boolean
}> = ({ mark, productColorHex, placeholder, showThreadEffect }) => {
  const { firstLine, secondLine, font, threadColor } = mark.values
  const isPlaceholder = !firstLine.trim() && !secondLine?.trim()

  const hasContrast =
    isPlaceholder || !threadColor
      ? true
      : isSufficientEmbroideryColorContrast(productColorHex, threadColor.hexCode)

  return (
    <Text
      $color={threadColor?.hexCode ?? 'inherit'}
      $fontFamily={deriveTextEmbroideryFontFamilyFromStyle(font?.designFile ?? 'inherit')}
      $fontScale={font?.scale ?? 1}
      $warning={!hasContrast}
      $showThreadEffect={showThreadEffect}
    >
      <span>{isPlaceholder ? placeholder : firstLine}</span>
      {secondLine && <span>{secondLine}</span>}
    </Text>
  )
}

const GraphicMark: React.FC<{ mark: GraphicPreviewMark; showThreadEffect: boolean }> = ({
  mark,
  showThreadEffect,
}) => {
  if (mark.typeKey === 'icon') {
    const { icon } = mark.values
    if (!icon) return <GraphicPlaceholder />

    return (
      <IconImage
        alt=''
        src={icon.image.src}
        loading='default'
        widths={{ unit: 'px', sm: 80, md: 80 }}
        aspectRatios={{ sm: null, md: null }}
        transparentBackground
        $scale={Number.isNaN(icon.scale) ? 1 : icon.scale}
        $showThreadEffect={showThreadEffect}
      />
    )
  }

  const { imageSrc, isBlackAndWhite } = mark.values
  if (!imageSrc) return <GraphicPlaceholder />

  return (
    <LogoImage
      $grayscale={isBlackAndWhite}
      $showThreadEffect={showThreadEffect}
      src={imageSrc}
      alt=''
      loading='default'
      widths={{ unit: 'px', sm: 112, md: 112 }}
      aspectRatios={{ sm: null, md: null }}
      transparentBackground
    />
  )
}
